import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { ErrorService } from "@/lib/errors/ErrorService";
import { storageService } from "@/services/storageService";
import { apiClient } from "@/lib/apiClient";
import { useBatchSynthesis } from "@/hooks/useBatchSynthesis";
import { useAllCases } from "@/hooks/useCases";
import { useQueryClient } from "@tanstack/react-query";
import { SaveToDestinationDialog } from "@/components/documents/destination/SaveToDestinationDialog";
import type { DestinationValue } from "@/components/documents/destination/destinationModel";
import {
  sanitizeFileName,
  generateDocxContent,
  generateDocxFromTimeline,
  cleanContentForTitle,
  cleanContentForStorage,
} from "../../utils/docxExport";
import type { ChatMessage } from "@/types/chat";
import type { CaseMetadata } from "@/services/folderHierarchyService";
import type { TimelineItem } from "../../types/session.types";

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

interface SaveToResearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: ChatMessage | null;
  /**
   * Fil chronologique complet de l'échange. Fourni, le DOCX embarque les
   * résultats de jurisprudence (synthèse, sous-questions, liens Judilibre) au
   * lieu du seul texte du message.
   */
  timeline?: TimelineItem[];
  caseId?: string;
  preselectedCaseId?: string;
}

/**
 * Enregistre une réponse de l'agent comme pièce d'un dossier.
 *
 * Le CHOIX de la destination vit dans `SaveToDestinationDialog`, partagé avec les
 * autres écrans. Ce module ne garde que ce qui lui est propre : fabriquer le DOCX
 * et écrire.
 *
 * ─── Ce qui change pour l'avocat (2026-09-15) ─────────────────────────────
 * L'onglet était FIGÉ à « Mes recherches juridiques » ; il est désormais
 * présélectionné mais modifiable, comme dans les autres écrans. La liste des
 * dossiers passe du panneau maison — champ de recherche et fermeture au clic
 * extérieur recodés ici — au composant standard du dépôt.
 *
 * `document_subcategory: 'analyse'` reste posé sous « Mes recherches juridiques »
 * (TICKET-278) : une réponse d'agent sauvegardée est un travail du cabinet, ni
 * décision brute ni doctrine publiée. Ailleurs la sous-catégorie est un attribut
 * libre que rien ne lit — on ne l'écrit pas.
 */
export function SaveToResearchDialog({
  open,
  onOpenChange,
  message,
  timeline,
  caseId: propCaseId,
  preselectedCaseId,
}: SaveToResearchDialogProps) {
  const queryClient = useQueryClient();
  const { data: cases = [] } = useAllCases();
  // Le dossier RÉELLEMENT choisi : `useBatchSynthesis` scope ses invalidations et
  // les métadonnées du job dessus.
  const [destination, setDestination] = useState<DestinationValue | null>(null);
  const initialCaseId = preselectedCaseId || propCaseId || undefined;
  const { submitBatches } = useBatchSynthesis(destination?.caseId ?? initialCaseId ?? "");
  const handleSelectionChange = useCallback((value: DestinationValue) => setDestination(value), []);

  const defaultTitle = useMemo(() => {
    if (!message?.content) return "";
    const clean = cleanContentForTitle(message.content);
    return clean.slice(0, 60) + (clean.length > 60 ? "..." : "");
  }, [message?.content]);

  const handleConfirm = async ({
    destination: target,
    name,
    subcategory,
  }: {
    destination: DestinationValue;
    name: string;
    subcategory: string | null;
  }) => {
    if (!message) return;
    const { caseId, category, folderPath } = target;
    let storagePath = "";

    try {
      const blob =
        timeline && timeline.length > 0
          ? await generateDocxFromTimeline(name, timeline)
          : await generateDocxContent(name, cleanContentForStorage(message.content));

      const documentId = crypto.randomUUID();
      const fileName = `${sanitizeFileName(name)}.docx`;
      const file = new File([blob], fileName, { type: DOCX_MIME, lastModified: Date.now() });

      const uploadResult = await storageService.uploadFile(file, { caseId, category });
      storagePath = (Array.isArray(uploadResult) ? uploadResult[0]?.path : uploadResult.path) ?? "";

      try {
        await apiClient.post(`/cases/${caseId}/documents`, {
          id: documentId,
          name: fileName,
          file_path: storagePath,
          document_type: DOCX_MIME,
          document_category: category,
          // Sous « Partie adverse », le tag du dossier choisi commande : c'est lui
          // que l'onglet filtre, une pièce sans tag n'y apparaîtrait nulle part.
          document_subcategory: subcategory ?? (category === "recherche" ? "analyse" : null),
          folder_path: folderPath,
          folder_depth: folderPath.length,
          parent_folder_path: folderPath.slice(0, -1),
          display_folder_path: folderPath.length > 0 ? folderPath.join("/") : null,
          is_folder: false,
          file_size: file.size,
          piece_description: `Recherche sauvegardée depuis Case Discovery - ${new Date().toLocaleDateString("fr-FR")}`,
        });
      } catch (insertError) {
        ErrorService.handle(insertError, {
          context: { action: "save_research_insert", case_id: caseId, document_id: documentId },
          silent: true,
        });
        // L'objet S3 est déjà écrit : sans ce retrait, il resterait orphelin,
        // facturé et invisible (aucune ligne ne le référence).
        await storageService.removeFiles(storagePath);
        throw insertError;
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["folder-tree", caseId, category] }),
        queryClient.invalidateQueries({ queryKey: ["folder-contents", caseId, category] }),
        queryClient.invalidateQueries({ queryKey: ["case-documents", caseId, category] }),
        queryClient.invalidateQueries({ queryKey: ["case-documents"] }),
      ]);

      const selectedCase = cases.find((c) => c.id === caseId);
      if (selectedCase) {
        const metadata: CaseMetadata = {
          dossierID: caseId,
          dossierName: selectedCase.title || "Dossier sans titre",
          clientName: selectedCase.client_name || "Client non spécifié",
          lawyerName: selectedCase.assigned_to || "Non assigné",
          typeDeProcedure: selectedCase.procedure_type || "Type non spécifié",
        };
        // `generateTitle: false` : on conserve le titre saisi par l'avocat.
        submitBatches([{ documentId, fileName }], metadata, { category, generateTitle: false }).catch(
          (err) => {
            ErrorService.handle(err, {
              context: { action: "save_research_synthesis", case_id: caseId, document_id: documentId },
              silent: true,
            });
          },
        );
      }

      toast.success("Recherche sauvegardée", {
        description: "Le document est disponible dans le dossier",
      });
    } catch (error) {
      ErrorService.handle(error, { context: { action: "save_research", case_id: caseId } });
      // Relancé : le dialogue partagé garde la fenêtre ouverte, choix intacts.
      throw error;
    }
  };

  return (
    <SaveToDestinationDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Sauvegarder dans le dossier"
      description="Cette réponse sera enregistrée comme document du dossier."
      confirmLabel="Sauvegarder"
      nameField={{
        label: "Titre du document",
        placeholder: "Ex. Analyse de la prescription",
        initialValue: defaultTitle,
      }}
      initialCaseId={initialCaseId}
      defaultCategory="recherche"
      onSelectionChange={handleSelectionChange}
      onConfirm={handleConfirm}
    />
  );
}
